import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cph_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Short-lived signed download token (kept out of long-lived access token exposure in URLs)
let _dlToken = null;
let _dlExpiry = 0;

export async function ensureDownloadToken() {
  const now = Date.now();
  if (_dlToken && now < _dlExpiry - 60_000) return _dlToken;
  const { data } = await api.get("/auth/download-token");
  _dlToken = data.token;
  _dlExpiry = now + (data.expires_in || 1200) * 1000;
  return _dlToken;
}

export function clearDownloadToken() {
  _dlToken = null;
  _dlExpiry = 0;
}

export function fileUrl(fileId, w) {
  return `${API}/files/${fileId}?dl=${_dlToken || ""}${w ? `&w=${w}` : ""}`;
}

export function pdfUrl(visitId) {
  return `${API}/visits/${visitId}/pdf?dl=${_dlToken || ""}`;
}

export function evidencePackUrl(visitId) {
  return `${API}/visits/${visitId}/evidence-pack?dl=${_dlToken || ""}`;
}

// Photo-heavy PDFs are built in the background so they never stall the server.
// Ask the server to build/cache the PDF; returns "ready" | "generating" | "error".
export async function preparePdf(visitId) {
  const { data } = await api.post(`/visits/${visitId}/pdf/prepare`);
  return data.status;
}

// Poll prepare until ready, then fetch the cached PDF as a blob and trigger a download.
export async function exportPdf(visitId, { maxWaitMs = 180000, filename = "report.pdf" } = {}) {
  const start = Date.now();
  let status = await preparePdf(visitId);
  while (status !== "ready") {
    if (status === "error") throw new Error("build-failed");
    if (Date.now() - start > maxWaitMs) throw new Error("timeout");
    await new Promise((r) => setTimeout(r, 2500));
    status = await preparePdf(visitId);
  }
  await ensureDownloadToken().catch(() => {});
  const res = await api.get(pdfUrl(visitId), { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

export function dampReportUrl(visitId) {
  return `${API}/visits/${visitId}/damp-report-pdf?dl=${_dlToken || ""}`;
}

// Ensure a fresh download token, then open the signed URL (used for PDF / ZIP buttons)
export async function openSigned(builder, arg) {
  await ensureDownloadToken().catch(() => {});
  window.open(builder(arg), "_blank", "noopener,noreferrer");
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
